import { ZodiacSign, AgeDetail } from '../types';
import { ZODIAC_ORDER } from '../constants';

export const calculateZodiac = (date: Date): ZodiacSign => {
  const day = date.getDate();
  const month = date.getMonth() + 1; // 1-12

  if ((month === 3 && day >= 21) || (month === 4 && day <= 19)) return ZodiacSign.Aries;
  if ((month === 4 && day >= 20) || (month === 5 && day <= 20)) return ZodiacSign.Taurus;
  if ((month === 5 && day >= 21) || (month === 6 && day <= 20)) return ZodiacSign.Gemini;
  if ((month === 6 && day >= 21) || (month === 7 && day <= 22)) return ZodiacSign.Cancer;
  if ((month === 7 && day >= 23) || (month === 8 && day <= 22)) return ZodiacSign.Leo;
  if ((month === 8 && day >= 23) || (month === 9 && day <= 22)) return ZodiacSign.Virgo;
  if ((month === 9 && day >= 23) || (month === 10 && day <= 22)) return ZodiacSign.Libra;
  if ((month === 10 && day >= 23) || (month === 11 && day <= 21)) return ZodiacSign.Scorpio;
  if ((month === 11 && day >= 22) || (month === 12 && day <= 21)) return ZodiacSign.Sagittarius;
  if ((month === 12 && day >= 22) || (month === 1 && day <= 19)) return ZodiacSign.Capricorn;
  if ((month === 1 && day >= 20) || (month === 2 && day <= 18)) return ZodiacSign.Aquarius;
  return ZodiacSign.Pisces;
};

export const calculateMoonSign = (date: Date): ZodiacSign => {
    // Approximate Moon Sign Calculation using J2000 epoch
    // Mean daily motion of Moon: 13.17639 degrees
    // Moon longitude at J2000.0 (Jan 1 12:00 UTC): approx 218.3 degrees
    
    // Days since J2000
    const j2000 = new Date('2000-01-01T12:00:00Z');
    const daysSince = (date.getTime() - j2000.getTime()) / (1000 * 60 * 60 * 24);
    
    const deg = (218.316 + 13.17639 * daysSince) % 360;
    const normalizedDeg = deg < 0 ? deg + 360 : deg;
    
    // 0 = Aries, 30 = Taurus, etc.
    const signIndex = Math.floor(normalizedDeg / 30);
    return ZODIAC_ORDER[signIndex % 12];
};

export const calculateRisingSign = (date: Date): ZodiacSign | null => {
    // Requires approximate time. If date has no time set (defaults to 00:00 local usually), 
    // this might be inaccurate. We assume the passed date object includes the user's birth time.
    
    // Simplified approximation:
    // Ascendant changes approx 1 sign every 2 hours.
    // At 6 AM (Sunrise approx), Ascendant is usually the same as Sun Sign.
    // This ignores latitude/season variations but works for a general visualizer.
    
    const sunSign = calculateZodiac(date);
    const sunSignIndex = ZODIAC_ORDER.indexOf(sunSign);
    
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const timeDecimal = hours + (minutes / 60);
    
    // Offset from 6 AM
    // If born at 8 AM -> +2 hours -> +1 sign
    // If born at 4 AM -> -2 hours -> -1 sign
    const offsetHours = timeDecimal - 6;
    const signOffset = Math.round(offsetHours / 2);
    
    let ascIndex = (sunSignIndex + signOffset) % 12;
    if (ascIndex < 0) ascIndex += 12;
    
    return ZODIAC_ORDER[ascIndex];
};

export const getChineseZodiacFromYear = (year: number): string => {
  const animals = ['Monkey', 'Rooster', 'Dog', 'Pig', 'Rat', 'Ox', 'Tiger', 'Rabbit', 'Dragon', 'Snake', 'Horse', 'Goat'];
  const startYear = 1900; // A year of the Rat
  return animals[(year - startYear) % 12];
}

export const getChineseZodiac = (date: Date): string => {
  const year = date.getFullYear();
  return getChineseZodiacFromYear(year);
};

export const calculateAge = (birthDate: Date): AgeDetail => {
  const now = new Date();
  const diff = now.getTime() - birthDate.getTime();

  const totalMilliseconds = diff;
  const totalSeconds = Math.floor(diff / 1000);
  const totalMinutes = Math.floor(totalSeconds / 60);
  const totalHours = Math.floor(totalMinutes / 60);
  const totalDays = Math.floor(totalHours / 24);
  const totalWeeks = Math.floor(totalDays / 7);
  const totalMonthsApprox = Math.floor(totalDays / 30.44);

  // Precise breakdown
  let years = now.getFullYear() - birthDate.getFullYear();
  let months = now.getMonth() - birthDate.getMonth();
  let days = now.getDate() - birthDate.getDate();
  let hours = now.getHours() - birthDate.getHours();
  let minutes = now.getMinutes() - birthDate.getMinutes();
  let seconds = now.getSeconds() - birthDate.getSeconds();

  if (seconds < 0) {
    minutes--;
    seconds += 60;
  }
  if (minutes < 0) {
    hours--;
    minutes += 60;
  }
  if (hours < 0) {
    days--;
    hours += 24;
  }
  if (days < 0) {
    months--;
    // Get days in previous month
    const prevMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    days += prevMonth.getDate();
  }
  if (months < 0) {
    years--;
    months += 12;
  }

  return {
    years,
    months,
    days,
    hours,
    minutes,
    seconds,
    totalMonths: totalMonthsApprox,
    totalWeeks,
    totalDays,
    totalHours,
    totalSeconds,
    totalMilliseconds,
  };
};

export const calculateSunriseSunset = (date: Date, lat: number, lng: number) => {
    // A simplified algorithmic approximation for demo purposes since we don't have an astronomy library
    const now = new Date();
    
    const baseSunrise = new Date(now);
    baseSunrise.setHours(6, 0, 0, 0);
    
    const baseSunset = new Date(now);
    baseSunset.setHours(18, 0, 0, 0);

    return {
        sunrise: baseSunrise,
        sunset: baseSunset,
        dayLength: 12 // hours
    };
};